import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * The axis editor's parts.
 *
 * Axes are **not identity**: editing them never invalidates a reference and never bumps a
 * version (PRD 02 §6). The copy that says so is the surface's; what lives here is the shape
 * — a labelled row of removable option chips, and the one control that grows it.
 *
 * The row is React Aria's `TagGroup`, which is what a row of removable chips actually is:
 * one tab stop, arrow keys between the chips, **Backspace or Delete removes the focused
 * one**, and focus lands on a sensible neighbour afterwards instead of falling to the top of
 * the document. Removal is still declared per chip, so `OptionTagProps` is unchanged apart
 * from the `id` a collection needs.
 *
 * A tag in a group that selects nothing publishes no `data-hovered`, so the chip's own hover
 * stays a `:hover` in the sheet — RAC does not own that one.
 *
 * Removing an option keeps the variants already made for it. The chip's remove button says
 * "remove", not "delete", for exactly that reason.
 */
import { createContext, useContext, useEffect, useRef } from 'react';
import { Button as AriaButton, Tag, TagGroup, TagList } from 'react-aria-components';
import { CrossGlyph, PlusGlyph } from '../glyphs.js';
/**
 * A tag group hands `onRemove` a set of keys, but the design declares removal on the chip.
 * The row keeps a map from key to handler and dispatches into it — the one seam between
 * RAC's collection model and this API.
 */
const RemoveHandlers = createContext(null);
/** One option on an axis: its word, its remove control, and — while renaming — its field. */
export function OptionTag({ id, children, removeLabel, onRemove, onRename, renameLabel, field, }) {
    const handlers = useContext(RemoveHandlers);
    // Idempotent by construction — the same key always maps to this chip's handler — so it is
    // safe under a double render, and it is only ever read from an event.
    handlers?.set(id, onRemove);
    if (field !== undefined) {
        return (_jsx(Tag, { id: id, className: "tag-opt is-new", textValue: id, children: _jsx(AxisInlineField, { ...field, className: "tag-opt-input" }) }));
    }
    return (_jsxs(Tag, { id: id, className: "tag-opt", textValue: typeof children === 'string' ? children : id, children: [onRename === undefined ? (children) : (_jsx(AriaButton, { slot: null, className: "tag-opt-name", "aria-label": renameLabel, onPress: onRename, children: children })), _jsx(AriaButton, { slot: "remove", className: "x", "aria-label": removeLabel, children: _jsx(CrossGlyph, {}) })] }));
}
export function AxisRow({ name, rowLabel, count, children, add, menu }) {
    const handlers = useRef(new Map()).current;
    return (_jsxs("div", { className: "axis", children: [_jsxs("div", { className: "a-top", children: [_jsx("span", { className: "lbl", children: name }), count !== undefined && _jsx("span", { className: "cap", children: count }), menu] }), _jsx(RemoveHandlers.Provider, { value: handlers, children: _jsxs(TagGroup, { className: "chiprow", onRemove: (keys) => {
                        for (const key of keys)
                            handlers.get(key)?.();
                    }, children: [_jsx(TagList, { className: "taglist", "aria-label": rowLabel ?? String(name), children: children }), add] }) })] }));
}
/**
 * The three keys every inline field in this editor keeps.
 *
 * Enter commits, Escape cancels, and blurring commits whatever is there, because a field
 * that silently discards what you typed when you clicked away is worse than one that keeps
 * it. Deliberately a plain input rather than a RAC `TextField`: it has no label row, no help
 * and no error — it is a word that happens to accept typing, and that contract is all three
 * of its callers need.
 *
 * On a rename the text arrives selected, so typing replaces it and the caret is still there
 * to edit from.
 */
function AxisInlineField({ className, label, placeholder, defaultValue, onCommit, onCancel, }) {
    const input = useRef(null);
    // Focus and selection together, on mount, from one place. `autoFocus` alone lands the
    // caret but leaves the word intact, so a rename would start by asking you to clear it;
    // and inside a RAC collection it is React's own autofocus that reaches commit first.
    useEffect(() => {
        input.current?.focus();
        input.current?.select();
    }, []);
    return (_jsx("input", { ref: input, className: className, type: "text", "aria-label": label, defaultValue: defaultValue, placeholder: placeholder, onKeyDown: (event) => {
            // A field owns every key struck in it. Without this, Backspace in a chip being
            // renamed reaches the `TagGroup` above it and removes the option instead.
            event.stopPropagation();
            if (event.key === 'Enter') {
                event.preventDefault();
                onCommit(event.currentTarget.value);
            }
            if (event.key === 'Escape') {
                event.preventDefault();
                onCancel();
            }
        }, onBlur: (event) => {
            const value = event.currentTarget.value.trim();
            if (value === '')
                onCancel();
            else
                onCommit(value);
        } }));
}
/** A chip that is a field — an option being typed, or one being renamed in place. */
export function NewOptionTag(props) {
    return (_jsx("span", { className: "tag-opt is-new", children: _jsx(AxisInlineField, { ...props, className: "tag-opt-input" }) }));
}
/**
 * The axis\u2019 own name, retyped where it stands (`i-0005`).
 *
 * Not a chip: the name is a caption above the row, not one of the words in it, and a chip
 * here would read as a fifth option. It keeps the caption\u2019s type and takes its rule
 * from the field it is.
 */
export function AxisNameField(props) {
    return _jsx(AxisInlineField, { ...props, className: "lbl a-name" });
}
/** The quiet "add an option" button that ends every chip row. */
export function AddOptionButton({ children, onClick, disabled = false, }) {
    return (_jsxs(AriaButton, { className: "btn btn-quiet btn-sm", onPress: onClick, isDisabled: disabled, children: [_jsx(PlusGlyph, {}), _jsx("span", { children: children })] }));
}
//# sourceMappingURL=AxisEditor.js.map